import { ChevronRight, File, Folder } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function FileInfo({
  info,
  apiUrl,
  setForceRefresh,
  initiallyCollapsed = true,
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const isDir = info.files !== undefined;

  const formatSize = (size: number) => {
    if (size >= 1073741824) {
      return (size / 1073741824).toFixed(2) + " GB";
    } else if (size >= 1048576) {
      return (size / 1048576).toFixed(2) + " MB";
    } else if (size >= 1024) {
      return (size / 1024).toFixed(2) + " KB";
    } else {
      return size + " B";
    }
  };

  const getFileDownloadUrl = (path: string) => {
    return apiUrl + "/files/download?path=" + encodeURIComponent(path);
  };

  const deleteFile = (e, info) => {
    e.preventDefault();
    let confirmText: string;
    if (info.files === undefined) {
      confirmText = t("file_download.delete_file_confirmation", {
        0: info.basename,
      });
    } else {
      confirmText = t("file_download.delete_folder_confirmation", {
        0: info.basename,
      });
    }
    if (window.confirm(confirmText)) {
      fetch(apiUrl + "/files/delete?filename=" + encodeURIComponent(info.path))
        .then((res) => res.json())
        .then(
          (result) => {
            console.info("File deleted:", result);
            info.hidden = true;
            setForceRefresh((prev: number) => prev + 1);
          },
          (error) => {
            console.error("Error deleting file:", error);
            alert("Error deleting file: " + error.message);
          }
        );
    }
  };

  return (
    <div className="px-4">
      {isDir ? (
        <>
          <div
            className="flex items-center space-x-2 rounded-lg hover:bg-primary-800 px-3 py-1 cursor-pointer"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {isDir ? (
              <div className="flex items-center space-x-2">
                <ChevronRight
                  className="h-3 w-3 invert opacity-50 transition-all"
                  style={{
                    transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
                  }}
                />
                <Folder className="h-6 w-fit invert opacity-50" />
              </div>
            ) : (
              <File className="h-6 w-fit invert opacity-50" />
            )}
            <p>{info.basename}</p>
            <p className="text-primary-200">({formatSize(info.size)})</p>
            <a
              href={getFileDownloadUrl(info.path)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center border justify-center px-3 py-0.5 rounded-md whitespace-nowrap shadow-sm hover:bg-primary-400"
              title="Download this folder as a ZIP file"
              onClick={(e) => e.stopPropagation()}
            >
              {t("file_download.download_folder_button")}
            </a>
          </div>
          {collapsed ? null : (
            <div>
              {info.files
                .filter(
                  (item) =>
                    item.files !== undefined &&
                    item.files.length &&
                    !item.hidden
                )
                .map((file, index) => (
                  <FileInfo
                    key={index}
                    info={file}
                    apiUrl={apiUrl}
                    setForceRefresh={setForceRefresh}
                  />
                ))}
              {info.files
                .filter((item) => item.files === undefined && !item.hidden)
                .map((file, index) => (
                  <FileInfo
                    key={index}
                    info={file}
                    apiUrl={apiUrl}
                    setForceRefresh={setForceRefresh}
                  />
                ))}
            </div>
          )}
        </>
      ) : (
        <a
          href={getFileDownloadUrl(info.path)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center space-x-2 rounded-lg hover:bg-primary-800 py-[3px]"
        >
          <File className="h-6 w-fit invert opacity-50" />
          <p className="text-cyan-500 underline">{info.basename}</p>
          <p className="text-primary-200">({formatSize(info.size)})</p>
          <button
            className="flex items-center border justify-center px-3 py-0.5 rounded-md whitespace-nowrap shadow-sm bg-rose-900 opacity-60 hover:bg-rose-700 hover:opacity-100"
            title="Delete this file"
            onClick={(e) => deleteFile(e, info)}
          >
            {t("file_download.delete_file_button")}
          </button>
        </a>
      )}
    </div>
  );
}
